import React, {FC, useMemo} from 'react';
import moment, {Moment} from 'moment';
import {getLocale} from '@epic-core/common/dist';
import {makeStyles} from '@material-ui/core/styles';

import Month from './Month';

import {DateMoment} from '../../../../../types';

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        flexWrap: 'wrap',
        alignContent: 'stretch',
        margin: theme.spacing(2, 'auto', 'auto')
    }
}));

const makeYearArray = (year: number): Moment[] =>
    new Array(12).fill(null).map((_, index) => moment(new Date(year, index, 1, 0, 0, 0)));

export interface MonthCalendar {
    startDate: DateMoment;
    endDate: DateMoment;
    currentYear: number;
    min?: DateMoment;
    max?: DateMoment;
    locale?: string;
    isPreselection?: boolean;
    onChange: (date: DateMoment) => void;
}

const MonthCalendar: FC<MonthCalendar> = ({
    startDate: startDateProps,
    endDate: endDateProps,
    currentYear,
    min,
    max,
    locale,
    isPreselection,
    onChange
}: MonthCalendar) => {
    const startDate = moment(startDateProps);
    const endDate = moment(endDateProps);

    const classes = useStyles();

    const monthsPropsList = useMemo(() => {
        const formatter = new Intl.DateTimeFormat(getLocale(), {month: 'short'});

        return makeYearArray(currentYear).map(month => ({
            singleSelect: endDate.isSame(startDate, 'month'),
            preselected: isPreselection,
            rangeStart: startDate.isSame(month, 'month'),
            rangeEnd: endDate.isSame(month, 'month'),
            inRange: month.isBetween(startDate, endDate, 'month', '()'),
            selected: startDate.isSame(month, 'month') || endDate.isSame(month, 'month'),
            disabled:
                month.isSameOrAfter(max, 'month') || (min && month.isSameOrBefore(min, 'month')),
            children: formatter.format(month.toDate()),
            key: month.month(),
            onClick: () => onChange(month)
        }));
    }, [locale, currentYear, startDate, endDate]);

    return (
        <div className={classes.container}>
            {monthsPropsList.map(props => (
                <Month key={props.key} {...props} />
            ))}
        </div>
    );
};

export default MonthCalendar;
